import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ActionIcon, Button, Divider, Group, Image, Menu, Title, Tooltip } from "@mantine/core";
import { IconStack2, IconDatabase, IconMapPin, IconVersions, IconSettings, IconChevronDown, IconPencilMinus, IconDotsVertical, IconLayoutSidebarLeftCollapse, IconLayoutSidebarLeftExpand, IconHome, IconInfoSquareRounded, IconMessageShare, } from "@tabler/icons-react";
import { useMantineColorScheme } from "@mantine/core";
import { useDisclosure, useElementSize, useViewportSize } from "@mantine/hooks";
import { useAppDispatch, useAppSelector } from "../hooks/redux.js";
import { getInitState } from "../reducers/initReducer.js";
import { useStyleEditor } from "./StyleEditorContext.js";
import { StyleControls } from "./StyleControls.js";
import { RenameStyleModal } from "./RenameStyleModal.js";
import { useEffect, useState } from "react";
import { debounce, noop } from "es-toolkit";
import { BLACK_IN_LIGHT_MODE, WHITE_IN_LIGHT_MODE } from "@mapka/style-common";
import { toggleChat } from "../reducers/chatReducer.js";
import { useIntl } from "react-intl";
import { useChatOpen } from "../hooks/assistant.js";
import { FEEDBACK_CONTROL_ID } from "../constants/events.js";
const PanelNav = ({ view, activeView, Icon, label, onViewChange, iconOnly }) => {
    const isActive = view === activeView;
    const style = isActive
        ? {
            color: WHITE_IN_LIGHT_MODE,
            backgroundColor: BLACK_IN_LIGHT_MODE,
        }
        : {};
    const handleNavClick = () => {
        if (view === activeView) {
            onViewChange("ai");
        }
        else {
            onViewChange(view);
        }
    };
    return iconOnly
        ? (_jsx(Tooltip, { label: label, children: _jsx(ActionIcon, { variant: "light", color: "dark", size: 32, style: style, onClick: handleNavClick, "aria-label": label, children: _jsx(Icon, { size: 24 }) }) }))
        : (_jsx(Button, { size: "sm", variant: "light", color: "dark", style: style, onClick: handleNavClick, leftSection: _jsx(Icon, { size: 24 }), children: label }));
};
const ActionControl = ({ Icon, label, action }) => {
    return (_jsx(Tooltip, { label: label, children: _jsx(ActionIcon, { size: 32, variant: "transparent", color: "black", "aria-label": label, onClick: action, children: _jsx(Icon, { size: 24 }) }) }));
};
const MainMenu = ({ onNavigateBack }) => {
    const intl = useIntl();
    const { colorScheme } = useMantineColorScheme();
    const goToOverviewLabel = intl.formatMessage({ defaultMessage: "Go to Overview", id: "menu.goToOverview" });
    const helpAndResourcesLabel = intl.formatMessage({ defaultMessage: "Help and resources", id: "menu.helpAndResources" });
    const shareFeedbackLabel = intl.formatMessage({ defaultMessage: "Share feedback", id: "menu.shareFeedback" });
    const logoSrc = colorScheme === "dark"
        ? "/icon256-dark.png"
        : "/icon256-light.png";
    return (_jsxs(Menu, { shadow: "md", position: "bottom-start", children: [_jsx(Menu.Target, { children: _jsxs(Group, { gap: 4, wrap: "nowrap", style: { cursor: "pointer" }, children: [_jsx(Image, { src: logoSrc, alt: "Mapka", height: "32px" }), _jsx(ActionIcon, { size: 16, variant: "transparent", color: "gray", component: "span", children: _jsx(IconChevronDown, {}) })] }) }), _jsxs(Menu.Dropdown, { children: [_jsx(Menu.Item, { leftSection: _jsx(IconHome, { size: 20 }), onClick: onNavigateBack, children: goToOverviewLabel }), _jsx(Menu.Divider, {}), _jsx(Menu.Item, { leftSection: _jsx(IconInfoSquareRounded, { size: 20 }), children: helpAndResourcesLabel }), _jsx(Menu.Item, { id: FEEDBACK_CONTROL_ID, leftSection: _jsx(IconMessageShare, { size: 20 }), children: shareFeedbackLabel })] })] }));
};
const getInitialChatWidth = () => {
    const chatView = document.querySelector("#mapka-ai-view");
    if (chatView) {
        return chatView.getBoundingClientRect().width;
    }
    return 320;
};
const panelGap = 16;
const estimatedPanelNavWidth = 680;
export const StyleEditorHeader = ({ view: activeView, onViewChange, onNavigateBack, onSave, onPublish, onCreate, onShare, }) => {
    const intl = useIntl();
    const initState = useAppSelector(getInitState);
    const { mapkaAccountName } = useStyleEditor();
    const chatOpen = useChatOpen();
    const { ref: styleControlsRef, width: styleControlsWidth } = useElementSize();
    const { width } = useViewportSize();
    const [chatWidth, setChatWidth] = useState(getInitialChatWidth());
    const isOwner = Boolean(mapkaAccountName && initState.accountName === mapkaAccountName);
    useEffect(() => {
        const handleResize = debounce((entries) => {
            const [entry] = entries;
            if (entry.target.id === "mapka-ai-view") {
                setChatWidth(entry.contentRect.width);
            }
        }, 20);
        const resizer = new ResizeObserver(handleResize);
        const chatView = document.querySelector("#mapka-ai-view");
        if (chatView) {
            resizer.observe(chatView);
        }
        return () => {
            resizer.disconnect();
        };
    }, []);
    const [renameOpened, { open: openRename, close: closeRename }] = useDisclosure(false);
    const dispatch = useAppDispatch();
    const handleCollapseChat = () => {
        dispatch(toggleChat());
    };
    const navItems = [
        { view: "layers", Icon: IconStack2, label: intl.formatMessage({ defaultMessage: "Layers", id: "label.layers" }) },
        { view: "sources", Icon: IconDatabase, label: intl.formatMessage({ defaultMessage: "Sources", id: "label.sources" }) },
        { view: "markers", Icon: IconMapPin, label: intl.formatMessage({ defaultMessage: "Markers", id: "label.markers" }) },
        { view: "versions", Icon: IconVersions, label: intl.formatMessage({ defaultMessage: "Versions", id: "label.versions" }) },
        { view: "settings", Icon: IconSettings, label: intl.formatMessage({ defaultMessage: "Settings", id: "label.settings" }) },
    ];
    const leftActions = [
        { Icon: IconLayoutSidebarLeftCollapse, label: intl.formatMessage({ defaultMessage: "Collapse chat", id: "label.collapseChat" }), action: handleCollapseChat, visible: chatOpen },
        { Icon: IconLayoutSidebarLeftExpand, label: intl.formatMessage({ defaultMessage: "Collapse chat", id: "label.collapseChat" }), action: handleCollapseChat, visible: !chatOpen },
    ].filter(a => a.visible);
    const iconOnly = (width - chatWidth - styleControlsWidth) < estimatedPanelNavWidth;
    const hasSpaceForTitle = chatWidth > 300;
    const styles = chatOpen && hasSpaceForTitle
        ? { position: "absolute", left: chatWidth - panelGap, top: 8 }
        : { position: "relative", paddingRight: 8 };
    const defaultStyleName = intl.formatMessage({
        defaultMessage: "New Map",
        id: "label.newMap",
    });
    const styleName = initState.style.scope === "public"
        ? defaultStyleName
        : initState.style.displayName;
    const renameLabel = isOwner
        ? intl.formatMessage({ defaultMessage: "Rename map", id: "label.renameMap" })
        : intl.formatMessage({ defaultMessage: "Save your map first to rename it", id: "tooltip.saveToRename" });
    return (_jsxs(Group, { component: "header", py: "xs", px: "md", children: [_jsxs(Group, { gap: 8, wrap: "nowrap", children: [_jsx(MainMenu, { onNavigateBack: onNavigateBack }), _jsx(Title, { order: 3, children: styleName }), _jsx(Tooltip, { label: renameLabel, disabled: isOwner, children: _jsx(ActionIcon, { variant: "subtle", color: "gray", component: "span", "aria-label": renameLabel, disabled: !isOwner, onClick: isOwner ? openRename : noop, children: _jsx(IconPencilMinus, {}) }) })] }), _jsxs(Group, { gap: 8, className: "mapka-style-editor-desktop-only", style: styles, children: [leftActions.map(({ Icon, label, action }) => (_jsx(ActionControl, { Icon: Icon, label: label, action: action }, label))), _jsx(Divider, { orientation: "vertical", pr: "4" }), navItems.map(({ view, Icon, label }) => (_jsx(PanelNav, { iconOnly: iconOnly, view: view, activeView: activeView, Icon: Icon, label: label, onViewChange: onViewChange }, view)))] }), _jsx(Group, { ref: styleControlsRef, className: "mapka-style-editor-desktop-only", ml: "auto", children: onSave && onCreate && (_jsx(StyleControls, { onSave: onSave, onPublish: onPublish, onCreate: onCreate, onShare: onShare })) }), _jsx(Group, { gap: 4, wrap: "nowrap", className: "mapka-style-editor-mobile-only", children: _jsx(ActionIcon, { size: 32, variant: "subtle", color: "gray", "aria-label": "Menu", children: _jsx(IconDotsVertical, { size: 20, stroke: 1.5 }) }) }), renameOpened && (_jsx(RenameStyleModal, { opened: true, onClose: closeRename, currentName: styleName }))] }));
};
